/**
 * HistoryModal - 수수료 변경 이력 공유 모달
 * toHeadquarters / riderToPartner 양쪽에서 사용
 */
import React from 'react';
import { formatCurrency } from '@/shared/utils/formatter';
import '../styles/HistoryModal.css';

export interface HistoryModalItem {
    applyDate: string;
    period?: string;
    commissionType: string;
    amount: number;
    modifier: string;
    insDate?: string;
}

interface HistoryModalProps {
    isOpen: boolean;
    title: string;
    name: string;
    historyData: HistoryModalItem[];
    onClose: () => void;
    showPeriodColumn?: boolean;
}

const getAmountUnit = (commissionType: string): string =>
    commissionType.includes('정률') ? '%' : '원';

const getPeriodTagClass = (period?: string): string => {
    if (!period) return 'period-tag';
    const isDaily = period === '일정산' || period === '선정산';
    return `period-tag ${isDaily ? 'period-tag-daily' : 'period-tag-weekly'}`;
};

const HistoryModal: React.FC<HistoryModalProps> = ({
                                                       isOpen,
                                                       title,
                                                       name,
                                                       historyData,
                                                       onClose,
                                                       showPeriodColumn = false,
                                                   }) => {
    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onClick={handleBackdropClick}
        >
            <div
                className="history-modal-container"
                onClick={(e) => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div className="history-modal-header">
                    <div className="history-modal-title-group">
                        <i className="fas fa-history"></i>
                        <div>
                            <h3>{title}</h3>
                            <p className="history-modal-subtitle">{name}</p>
                        </div>
                    </div>
                    <button
                        className="history-modal-close-btn"
                        onClick={onClose}
                        aria-label="닫기"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 본문 */}
                <div className="history-modal-body">
                    {historyData.length === 0 ? (
                        <div className="history-modal-empty">
                            <i className="fas fa-inbox"></i>
                            <p>변경 이력이 없습니다</p>
                        </div>
                    ) : (
                        <table className="history-modal-table">
                            <thead>
                            <tr>
                                <th>적용일자</th>
                                {showPeriodColumn && <th>정산구분</th>}
                                <th>수수료 유형</th>
                                <th>금액</th>
                                <th>수정자</th>
                                <th>등록일시</th>
                            </tr>
                            </thead>
                            <tbody>
                            {historyData.map((item, index) => (
                                <tr key={index}>
                                    <td className="text-center">{item.applyDate}</td>
                                    {showPeriodColumn && (
                                        <td className="text-center">
                                                <span className={getPeriodTagClass(item.period)}>
                                                    {item.period || '-'}
                                                </span>
                                        </td>
                                    )}
                                    <td className="text-center">{item.commissionType}</td>
                                    <td className="text-center history-amount">
                                        {formatCurrency(item.amount.toString())}{getAmountUnit(item.commissionType)}
                                    </td>
                                    <td className="text-center">{item.modifier}</td>
                                    <td className="text-center" style={{ color: 'var(--color-gray-500)', fontSize: '0.85rem' }}>
                                        {item.insDate || '-'}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* 푸터 */}
                <div className="history-modal-footer">
                    <span className="history-modal-footer-info">
                        총 <strong>{historyData.length}</strong>건
                    </span>
                    <button className="btn btn-secondary" onClick={onClose}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HistoryModal;
